import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { handleResponseLanguage } from '../../../../../i18n';
import { object } from 'yup';
import { string } from '../../../../common/src/constants/validationSchemas';
import { useTranslation } from 'react-i18next';

// Components
import { TextInput, DateInputV4 as DateInput } from 'wg-fe-ui';
import LargeButton from '../../../../common/src/components/LargeButton';
import SearchSelectInputV2 from '../../../../common/src/components/SearchSelectInputV2';
import LabelValue from '../offers/LabelValue';
import BackButton from '../../../../common/src/components/BackButton';

// Types
import { RootState } from '../../store/redux/reducers/rootReducer';
import { Change } from '../../../../../types/Form';
import { CancelledInsuranceValues } from '../../../../../types/Quotes';
import { reasonTypes } from '../../../../common/src/constants/terminationLetterConstants';
import { Button } from '@wegroup/design-system';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

interface Props {
  withOffer?: boolean;
  onContinue: (arg0: CancelledInsuranceValues) => void;
  onBack: () => void;
  onClose: () => void;
}

const CancelInsuranceForm: React.FC<React.PropsWithChildren<Props>> = ({
  withOffer,
  onContinue,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation();
  const { insuranceCompanies } = useSelector(
    (state: RootState) => state.offers,
  );

  const insuranceCompaniesOptions = insuranceCompanies
    .map(({ names, key }) => ({
      label: handleResponseLanguage(names),
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const schema = object({
    reasonOfTermination: string.required,
    insuranceCompany: string.required,
    policyNumber: string.required,
    expiryDate: string.notRequired,
  });

  const { handleSubmit, control } = useForm<CancelledInsuranceValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      insuranceCompany: '',
      policyNumber: '',
    },
  });

  return (
    <Wrapper>
      <Title>
        {t(
          'Please fill in the following information in order to generate your letter of termination',
        )}
      </Title>
      <Text>
        {t(
          'The letter of termination will be generated together with the offers at the end of the flow',
        )}
      </Text>
      <StyledLabelValue label={`${t('Reason for termination')} *`}>
        <Controller
          name="reasonOfTermination"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { error },
          }) => (
            <SearchSelectInputV2
              name={name}
              onSelected={(p) => onChange(p.value)}
              options={reasonTypes}
              value={reasonTypes.find(({ value: _value }) => value === _value)}
              error={error?.message}
              dataTestId="Prodfac_CancelInsuranceForm_ReasonOfTermination"
            />
          )}
        />
      </StyledLabelValue>

      <StyledLabelValue label={`${t('Current insurance company')} *`}>
        <Controller
          name="insuranceCompany"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { error },
          }) => (
            <SearchSelectInputV2
              name={name}
              onSelected={(p) => onChange(p.value)}
              options={insuranceCompaniesOptions}
              value={insuranceCompaniesOptions.find(
                ({ value: _value }) => value === _value,
              )}
              error={error?.message}
              dataTestId="Prodfac_CancelInsuranceForm_InsuranceCompany"
            />
          )}
        />
      </StyledLabelValue>
      <StyledLabelValue label={`${t('Policy number')} *`}>
        <Controller
          name="policyNumber"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { error },
          }) => (
            <StyledTextInput
              name={name}
              onChange={({ value }: Change) => onChange(value)}
              value={value}
              error={error?.message}
              dataTestId="Prodfac_CancelInsuranceForm_PolicyNumber"
            />
          )}
        />
      </StyledLabelValue>
      <StyledLabelValue label={t('Expiry date')}>
        <Controller
          name="expiryDate"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { error },
          }) => (
            <DateInput
              name={name}
              onChange={({ value }: Change) => onChange(value)}
              initialValue={value}
              error={error?.message}
              dataTestId="Prodfac_CancelInsuranceForm_ExpiryDate"
            />
          )}
        />
      </StyledLabelValue>
      <StyledLargeButton
        onClick={handleSubmit(onContinue)}
        fullwidth
        data-test-id="ADVISORY-CONVO_OFFERS_CANCEL-INSURANCE-FORM_CONTINUE"
      >
        {t('Continue')}
      </StyledLargeButton>
      {withOffer ? (
        <StyledBackButton onClick={onBack}>{t('Go back')}</StyledBackButton>
      ) : (
        <Button mt="4" variant="link" onClick={onClose}>
          {t('Close')}
        </Button>
      )}
    </Wrapper>
  );
};

const StyledBackButton = styled(BackButton)`
  margin-top: 0.8rem;
`;

const StyledLargeButton = styled(LargeButton)`
  margin-top: 0.8rem;
`;

const StyledTextInput = styled(TextInput)`
  height: unset;

  > label > input {
    margin-top: 0;
  }

  > div {
    margin: 0;
  }
`;

const StyledLabelValue = styled(LabelValue)`
  > p {
    margin-bottom: 0.8rem;
  }
  margin-bottom: 1.6rem;
`;

const Title = styled.h1`
  font-weight: 500;
  font-size: 1.8rem;
  margin-bottom: 1.6rem;
`;

const Text = styled.p`
  color: ${({ theme }) => theme.labels.guaranteeText};
  font-size: 1.6rem;
  line-height: 120%;
  margin-bottom: 2.4rem;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export default CancelInsuranceForm;
